import * as R from "ramda";
import * as CONSTANTS from "./constants";
import { BASE_DOMAIN } from "env";

export { useSessionStorage } from "./useSessionStorage";
export { useSessionJWT } from "./useSessionJWT";
export { useSessionCtx } from "./useSessionCtx";
export { redirectToIntegrator, urlHasQuery, redirectWindowLocation } from "./redirect";
export { useRedirect } from "./useRedirect";
export { usePostMessage } from "./usePostMessage";
export { useCardProvider } from "./useCardProvider";
export { compressImgFileIfLargerThanTarget } from "./imageResizer";
export { useNavigatorOnline } from "./useNavigatorOnline";
export { getDimensionsFromImgFile } from "./getDimensionsFromImgFile";
export { useAnalytics, AnalyticsProvider } from "./analytics/useAnalytics";
export { default as Analytics } from "./analytics/Analytics";
export { default as getFinalPaymentMethods } from "./getFinalPaymentMethods";
export { default as LanguageProvider } from "./LanguageProvider";

export * from "./strings";

export const FLOWS = CONSTANTS.FLOWS;
export const FLOW_STATE = CONSTANTS.FLOW_STATE;
export const FORMAT = CONSTANTS.FORMAT;

export const noop = () => {};
export const isNilOrEmpty = R.anyPass([R.isNil, R.isEmpty]);
export const exists = R.complement(isNilOrEmpty);
export const isString = R.is(String);
export const isFunction = R.pipe(R.type, R.identical("Function"));
export const callFuncOnNextTick = (f) => setTimeout(f, 0);

export const decodeJWT = (jwt) => {
  if (isNilOrEmpty(jwt)) return {};
  return JSON.parse(window.atob(jwt.split(".")[1]));
};

export const goodSessionData = (sessionData) => {
  return (
    !isNilOrEmpty(sessionData) &&
    !isNilOrEmpty(sessionData.integrator) &&
    !isNilOrEmpty(sessionData.account_id)
  );
};

export const getFormat = () => (inIframe() ? FORMAT.IFRAME : FORMAT.FULLPAGE);

export const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const parsePMIDFromFlowState = (flowState) => {
  if (!flowState) return null;
  const match = flowState.match(/\.complete\.(.*)$/);
  return match ? match[1] : null;
};

export const isCSSColor = (str) => {
  const { style: s } = new Option();
  s.color = str;
  return !isNilOrEmpty(s.color);
};

export const getFinalConfig = (flow, defaultConfig = {}, config = {}) =>
  R.mergeDeepRight(defaultConfig, R.defaultTo({}, R.prop(R.toUpper(flow), config)));

export const isArray = R.is(Array);
export const ensureArray = (v) => (isArray(v) ? v : [v]);

const isBool = R.compose(R.equals("Boolean"), R.type);
export const isBooleanTrue = R.allPass([isBool, R.equals(true)]);

const isTrueStringIgnoreCase = R.ifElse(
  R.is(String),
  R.compose(R.equals("true"), R.toLower),
  R.equals(true)
);

// returns true if input is string 'true' or boolean true, otherwise false
export const isTrue = R.anyPass([isBooleanTrue, isTrueStringIgnoreCase]);
export const isFalse = R.complement(isTrue);

const capitalizeFirstLetter = (s) => s[0].toUpperCase() + s.slice(1);

export const kebabToCapital = (kebab) => {
  return isNilOrEmpty(kebab)
    ? kebab
    : R.pipe(R.split("-"), R.map(capitalizeFirstLetter), R.join(" "))(kebab);
};

export const debugFn = function () {
  console.debug.apply(
    console,
    [`[${CONSTANTS.DEBUG_PREFIX}]`].concat(Array.prototype.slice.call(arguments))
  );
};

export const debuggableDomain = () => /dev|staging/.test(BASE_DOMAIN);
export const getDebugFn = (sessionCtxData = {}) => (sessionCtxData.debug ? debugFn : noop);

export const getTransactionDetailsFromFlowConfig = (flowConfig) => {
  if (!flowConfig || !flowConfig.transactionDetails) {
    return null;
  }
  const { transaction_amount, currency } = flowConfig.transactionDetails;
  if (!transaction_amount || !currency) {
    return null;
  }
  return { transaction_amount: transaction_amount, currency: currency.toUpperCase() };
};

export const walletHasEnoughToCoverAmount = (wallet, amount) => {
  const balance = wallet?.spendable?.balance || 0;
  const parsedAmount = parseInt(amount, 10);
  return balance >= parsedAmount;
};

export const hasNoAllowedPaymentOptions = (txnMthdsPayload, txnDetails = {}) => {
  // gonna check 3 things here:
  // 1: Do they have a 'non-wallet' known payment method they can use?
  // 2: Can they add a credit card or paypal account?
  // 3: Do they have a wallet? In addition to this, if we recieve txnDetails for the purchase, let's also check to make sure it's the right wallet and has enough to cover request
  // As a fallback, if any part of this errs to the catch, we'll default to 'false' and let it err on the server side
  try {
    const { providers, capabilities, balances, payment_methods } = txnMthdsPayload;
    const wallets = Object.values(payment_methods).filter((pm) => pm.provider === "wallet");

    // check for #1
    const nonWalletAllowedPaymentMethods = Object.values(payment_methods)
      .filter((pm) => pm.provider !== "wallet")
      .filter((pm) => capabilities.purchase.includes(pm.id)); // remove wallets and any pms not in capabilities.purchase
    const noKnownNonWalletPMs = nonWalletAllowedPaymentMethods.length === 0;

    // check for #2
    const ccAndPaypalNotAllowed =
      !providers.purchase.includes("card") && !providers.purchase.includes("paypal");

    // check for #3
    const userHasAWallet = wallets.length > 0; // may not be the right wallet, but if we don't get 'txnDetails', then at least we can say they have a wallet that may work
    let canUseWallet = userHasAWallet; // default to if they have a wallet or not
    const { transaction_amount, currency } = txnDetails;
    if (userHasAWallet && transaction_amount && currency) {
      // we have a wallet and we know how much is needed, so let's find and check spendable balance
      const wallet = balances[currency.toUpperCase()];
      if (wallet) {
        canUseWallet = walletHasEnoughToCoverAmount(wallet, transaction_amount);
      }
    }

    return noKnownNonWalletPMs && ccAndPaypalNotAllowed && !canUseWallet;
  } catch {
    return false; // our default if something fails, worst case is the user gets an error when trying to process their payment
  }
};

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const urlToFile = async (url, filename, mimeType) => {
  const res = await fetch(url); // currently being used for base64 strings, but can work for an external url as well
  const buf = await res.arrayBuffer();
  return new File([buf], filename, { type: mimeType });
};

/**
 * @name getLocaleTzAbbrFromDate
 * @param {Date} dateObj
 * @returns {(string | null)}
 * @description Will try to get the abbreviated timezone of the browser's location using toLocaleTimeString (if available).  If this doesn't work, we return null
 */
export function getLocaleTzAbbrFromDate(dateObj) {
  let response = null; // by default, if we can't sort the user's timezone abbreviation, we just send back null
  if (dateObj instanceof Date && dateObj.toLocaleTimeString) {
    const localeStr = dateObj.toLocaleTimeString(undefined, { timeZoneName: "short" }); // undefined let's us use what's set in the browser
    const tz = localeStr && localeStr.split(" ")[2];
    response = tz || response;
  }
  return response;
}

export const getIsMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    window?.navigator?.userAgent || ""
  );
};

export const capitalize = (str) => {
  // Check if the input is a string
  if (typeof str !== "string") {
    return str;
  }

  if (str.length < 1) {
    return str;
  }
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
};

export const parsePlaidDetailsFromFlowState = (flowState) => {
  if (!flowState) return {};
  const [name, state, details] = flowState.split(".");
  if (name === "plaid" && state === "error" && details === "INVALID_ADDRESS") {
    return { name, state, errorCode: details };
  } else if (name === "plaid" && state === "pending") {
    return { name, state, bankOwnerId: details };
  } else if (name === "plaid" && state === "complete") {
    return { name, state, bankAccountId: details };
  }
  return {};
};

export const parsePaypalDetailsFromFlowState = (flowState) => {
  if (!exists(flowState)) return {};
  const [name, state, pmId] = flowState.split(".");
  if (name !== "paypal") return {};
  return { name, state, pmId };
};
